import { ErrorHandler, NgModule, Optional, SkipSelf } from '@angular/core';
import { BreadcrumbService } from 'xng-breadcrumb';
import * as api from '../api';
import * as AppConfig from '../app.config';
import { APIThrottleService } from './api-throttle-service';
import { ErrorCatchingService } from './error-catching.service';
import { FacilitiesService, MyBreadcrumbService, ResidentsService, UserService, WindowRef, ChartFilterService } from './index';
import { AuthGuard } from './user/auth-guard.service';
import { SessionStateService } from './user/session-state.service';

/** ErrorCatchingService _is_ the ErrorHandler. Use the same instance. */
export function errorHandlerFactory(errorSvc: ErrorCatchingService): ErrorHandler {
    return errorSvc;
}

/** Configuration settings for the backend API */
export function backendConfigurationFactory(): api.Configuration {
    return new api.Configuration({basePath: AppConfig.baseApiUrl()});
}


/**
 * The Core module holds things that we import once when the app starts and never import anywhere else.
 *
 * Notes:
 * - This module is for services and related models. U/I belongs in the shared module.
 * - If a service is only needed within a single feature module, it should go there.
 *   This module is for things shared across multiple features.
 *
 * @see https://angular.io/docs/ts/latest/guide/ngmodule.html#!#core-module
 */
@NgModule({
    imports: [api.ApiModule.forRoot(backendConfigurationFactory)],
    providers: [
        // This module's services
        AuthGuard, ErrorCatchingService, FacilitiesService, ChartFilterService, MyBreadcrumbService,
        ResidentsService, SessionStateService, UserService, WindowRef, BreadcrumbService,
        APIThrottleService,
        {
            provide: ErrorHandler,
            useFactory: errorHandlerFactory,
            deps: [ErrorCatchingService]
        }
    ]
})
export class CoreModule {

    /** "One weird trick" to make sure we don't import CoreModule more than once. */
    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
        if (parentModule) {
            throw new Error(
                'CoreModule is already loaded. Import it in the AppModule only');
        }
    }
}
